<script setup lang="ts">
import { ref } from 'vue'

import Button from '@satch-ui/components/button/Button.vue'
import { t } from '@satch-ui/translations/translations'
import { addToCart } from '@shared/global-actions/actions/cart'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { z } from 'zod'

import { isHttpError } from '@fond-of/api-client'
import { CartWideIcon } from '@fond-of/satch-icons'
import { trackError } from '@fond-of/telemetry'

const props = defineProps<{
  productId: number
  showSidePanel?: boolean
}>()

const error = ref<string | undefined>('')

const schema = toTypedSchema(
  z.object({
    productId: z.number(),
    quantity: z.number(),
  })
)

const { handleSubmit, isSubmitting } = useForm({
  validationSchema: schema,
  initialValues: {
    productId: props.productId,
    quantity: 1,
  },
})

const onSubmit = handleSubmit(async (values) => {
  try {
    await addToCart(values.productId, values.quantity, {
      showSidePanel: props.showSidePanel,
    })
  } catch (exception) {
    trackError(exception)

    if (isHttpError(exception)) {
      const errors: { detail?: string }[] = await exception.response.json()
      error.value = errors?.[0]?.detail
    }

    if (!error.value) {
      error.value = t('validation.addToCart')
    }
  }
})
</script>

<template>
  <form
    class="w-full"
    @submit.prevent="onSubmit"
  >
    <input
      type="text"
      :value="props.productId"
      hidden
    />
    <Button
      id="addToCartBtn"
      class="w-full grow"
      type="submit"
      :disabled="isSubmitting"
    >
      {{ t('addToCart') }}

      <CartWideIcon class="size-4" />
    </Button>

    <div
      v-if="error"
      class="text-red w-full text-xs"
    >
      <span>
        {{ error }}
      </span>
    </div>
  </form>
</template>
